import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, BadgeCheck, Users, Clock, GitBranch, Layers, ShieldCheck } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { STEPS } from "@/data/content";

const MODEL_CARDS = [
  {
    icon: Layers,
    title: "A dedicated model, not a marketplace",
    copy: "Your team members are assigned to your business. They learn your systems, your standards and your customers, and they stay with you as you grow.",
  },
  {
    icon: ShieldCheck,
    title: "A management layer you don't build",
    copy: "Synergy Outsourcing provides the structure around your team: onboarding, coordination, performance oversight and a single point of accountability.",
  },
  {
    icon: Clock,
    title: "Aligned to your operating hours",
    copy: "Delivery arrangements are structured around Australian business hours or the hours that suit you best, matched to the demands of each role.",
  },
  {
    icon: GitBranch,
    title: "Built to scale with you",
    copy: "Start with one person. Add roles as the workload grows. The model flexes without re-recruiting or restructuring on your side.",
  },
];

const SHORTLIST_STATS = [
  { icon: CalendarCheck, value: "7 days", label: "From brief to interview-ready shortlist" },
  { icon: BadgeCheck, value: "$0", label: "Free to interview, no cost or obligation" },
  { icon: Users, value: "3 to 5", label: "Pre-screened candidates per role" },
];

function Shortlist() {
  return (
    <section className="py-14 sm:py-16" data-testid="shortlist-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid items-center gap-10 rounded-2xl border border-brand/25 bg-ink-2 p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand-soft px-4 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
                  Ready to interview in 7 days
                </span>
              </span>
              <h2 className="mt-6 font-display text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl">
                Meet your shortlist within a week. Free to interview.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-mist">
                Tell us the role today and we'll present pre-screened, dedicated professionals ready to interview
                within 7 days, at no cost and no obligation. You only commit when you meet someone you want on your
                team.
              </p>
              <Link
                to="/get-your-team"
                data-testid="shortlist-cta"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
              >
                Start your free interview
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="space-y-4">
              {SHORTLIST_STATS.map((s, i) => (
                <Reveal key={s.value} delay={i * 0.1}>
                  <div className="flex items-center gap-5 rounded-2xl border border-white/10 bg-ink p-6" data-testid={`shortlist-stat-${i}`}>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand">
                      <s.icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="font-display text-2xl font-extrabold text-brand">{s.value}</p>
                      <p className="text-sm text-mist">{s.label}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function HowItWorks() {
  useSEO(
    "How It Works | Managed Teams & Global Delivery Model | Synergy Outsourcing",
    "From role definition to a productive managed team: how Synergy Outsourcing sources, onboards and manages dedicated professionals for growing businesses."
  );
  return (
    <>
      <PageHero
        eyebrow="How it works"
        lines={["From role to", "productive team."]}
        copy="A clear, structured path from defining what you need to working with dedicated professionals, without building a management layer yourself."
      />
      <section className="py-14 sm:py-16" data-testid="hiw-steps">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative grid gap-10 md:grid-cols-4 md:gap-6">
            <div className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-transparent via-white/15 to-transparent md:block" aria-hidden="true" />
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.12}>
                <div className="relative">
                  <span className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-brand/40 bg-ink font-mono text-sm font-bold text-brand">
                    {s.num}
                  </span>
                  <h2 className="mt-5 font-display text-lg font-semibold text-white">{s.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Shortlist />
      <section className="bg-ink-2/40 py-14 sm:py-16" data-testid="hiw-model">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="The engagement model"
            title="What managed delivery actually means."
            copy="Outsourcing fails when nobody owns the outcome. Synergy Outsourcing is structured so that someone always does."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {MODEL_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="card-hover h-full rounded-2xl border border-white/10 bg-ink p-8">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft text-brand">
                    <c.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{c.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/get-your-team"
              data-testid="hiw-cta"
              className="group mt-12 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
            >
              Build Your Team
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
      <CTASection />
    </>
  );
}
